/**
 * Variant id + price resolution — client-safe, pure.
 *
 * Mirrors the server helpers in convex/helpers/productHelpers.ts
 * (sanitizeVariantId / resolveVariantPriceServer) so the product form, POS
 * variant dialog, storefront, stock batches, and the `variantStock` map all
 * agree byte-for-byte on variant ids and prices. Variant-id drift is the
 * classic bug here (stock shows 0 / the wrong batch drains) — always build ids
 * through `buildVariantId` and compare through `sanitizeVariantId`.
 */

/** Same sanitization as convex/helpers/productHelpers.ts `sanitizeVariantId`. */
export function sanitizeVariantId(variantId: string): string {
  // eslint-disable-next-line no-control-regex
  return variantId.replace(/[^\x20-\x7E]/g, "-").replace(/-{2,}/g, "-");
}

export type ItemDiscountType = "percentage" | "fixed";

/**
 * Apply a PER-ITEM discount to a base unit price and return the effective unit
 * price. The discount layers on top of whatever base is passed (retail OR the
 * wholesale price), so wholesale + discount compose. Result is clamped to ≥ 0
 * and rounded to a whole UGX. An absent/zero value returns the base unchanged.
 *
 * NOTE: convex/pos/sales.ts intentionally inlines this exact math (Convex
 * can't import the `@/` alias) — keep the two in sync if you change it.
 */
export function applyItemDiscount(
  baseUnitPrice: number,
  discountType?: ItemDiscountType | null,
  discountValue?: number | null,
): number {
  if (!discountType || !discountValue || discountValue <= 0) return baseUnitPrice;
  if (discountType === "percentage") {
    return Math.max(0, Math.round(baseUnitPrice * (1 - discountValue / 100)));
  }
  // "fixed" = a per-unit UGX reduction.
  return Math.max(0, Math.round(baseUnitPrice - discountValue));
}

/**
 * Build the canonical variant id used for stock batches + the `variantStock`
 * map: `variant-{colorId}{-designId}-{sizeOrStorage}`. `colorId` falls back to
 * "default" for storage-only (no-colour) variants. Returns undefined when there
 * is no size/storage value to key on.
 */
export function buildVariantId(
  colorId: string | undefined | null,
  designId: string | undefined | null,
  sizeOrStorage: string | undefined | null,
): string | undefined {
  if (!sizeOrStorage) return undefined;
  const color = colorId || "default";
  const designPart = designId ? `-${designId}` : "";
  return sanitizeVariantId(`variant-${color}${designPart}-${sizeOrStorage}`);
}

/**
 * Resolve a variant's selling price from `product.variants[]` (matched by
 * sanitized id), falling back to the product base price. A variant whose
 * `price` is unset or 0 also falls back to base — so existing products and
 * fashion items without per-variant pricing behave exactly as before.
 */
export function resolveVariantPrice(
  product: any,
  variantId: string | undefined | null,
): number {
  const base = Number(product?.price) || 0;
  if (!variantId) return base;
  const target = sanitizeVariantId(String(variantId));
  const variants: any[] =
    (product?.variants && product.variants.length ? product.variants : product?.fashionAttributes?.variants) || [];
  for (const v of variants) {
    if (!v?.id) continue;
    if (sanitizeVariantId(String(v.id)) === target) {
      return typeof v.price === "number" && v.price > 0 ? v.price : base;
    }
  }
  return base;
}

/** Convenience: resolve price by axes (colourId + size/storage [+ design]). */
export function resolveVariantPriceByAxes(
  product: any,
  colorId: string | undefined | null,
  sizeOrStorage: string | undefined | null,
  designId?: string | undefined | null,
): number {
  return resolveVariantPrice(product, buildVariantId(colorId, designId ?? undefined, sizeOrStorage));
}
